import { ApiReview } from '@storefronts/common-types'
import { useContext, useMemo } from 'react'
import { DEFAULT_LOCALE, DEFAULT_TIME_ZONE } from '../../../constants'
import {
    CustomizationContext,
    PRIMARY_FONT_CSS_VAR,
    PRIMARY_PAGE_TEXT_COLOR_VAR,
    SECONDARY_FONT_CSS_VAR,
} from '../../../context/CustomizationContextProvider'
import { Constants } from '../../../style/styleConstants'
import { getTypographyRoleStyle } from '../../../style/typography'
import { ReviewStar } from './ReviewStar'
import { scaledFontSize } from '../../../style/scrollingLayout'

function UserAvatarIcon() {
    return (
        <svg width="32" height="32" viewBox="0 0 32 32" fill="none" aria-hidden>
            <circle cx="16" cy="16" r="16" fill="#d1d5db" />
            <circle cx="16" cy="13" r="5" fill="#9ca3af" />
            <path
                d="M6 28c0-5.523 4.477-10 10-10s10 4.477 10 10"
                fill="#9ca3af"
            />
        </svg>
    )
}

export const ReviewCard: React.FC<{ review: ApiReview }> = ({ review }) => {
    const { reviewStarColorOverride, accentFillColor } =
        useContext(CustomizationContext)
    const starColor = reviewStarColorOverride || accentFillColor

    const formattedDate = useMemo(() => {
        const d = new Date(review.reviewedAt)
        return d.toLocaleString(DEFAULT_LOCALE, {
            month: 'long',
            day: 'numeric',
            year: 'numeric',
            timeZone: DEFAULT_TIME_ZONE,
        })
    }, [review.reviewedAt])

    return (
        <div style={{ marginTop: Constants.SECTION_BASE_SPACING_PX }}>
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    marginBottom: 6,
                }}
            >
                <UserAvatarIcon />
                <span
                    style={{
                        ...getTypographyRoleStyle('captionText', {
                            fontFamily: `var(${SECONDARY_FONT_CSS_VAR})`,
                            fontWeight: 500,
                        }),
                        fontSize: scaledFontSize(14),
                        color: `var(${PRIMARY_PAGE_TEXT_COLOR_VAR})`,
                    }}
                >
                    {review.user.name}
                </span>
            </div>
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    marginBottom: 8,
                }}
            >
                <ReviewStar rating={review.rating} />
                <span
                    style={{
                        ...getTypographyRoleStyle('badgeText', {
                            fontFamily: `var(${PRIMARY_FONT_CSS_VAR})`,
                            fontWeight: 700,
                        }),
                        fontSize: scaledFontSize(12),
                        color: starColor,
                    }}
                >
                    Verified Purchase
                </span>
            </div>
            {review.reviewContent.title && (
                <p
                    style={{
                        ...getTypographyRoleStyle('featureTitle', {
                            fontFamily: `var(${SECONDARY_FONT_CSS_VAR})`,
                            fontWeight: 600,
                            lineHeight: 1.35,
                        }),
                        fontSize: scaledFontSize(16),
                        color: `var(${PRIMARY_PAGE_TEXT_COLOR_VAR})`,
                        margin: '0 0 4px',
                    }}
                >
                    {review.reviewContent.title}
                </p>
            )}
            <p
                style={{
                    ...getTypographyRoleStyle('captionText', {
                        fontFamily: `var(${PRIMARY_FONT_CSS_VAR})`,
                        fontWeight: 400,
                    }),
                    fontSize: scaledFontSize(13),
                    opacity: 0.6,
                    color: `var(${PRIMARY_PAGE_TEXT_COLOR_VAR})`,
                    margin: '0 0 4px',
                }}
            >
                Reviewed on {formattedDate}
            </p>
            <p
                style={{
                    ...getTypographyRoleStyle('bodyText', {
                        fontFamily: `var(${PRIMARY_FONT_CSS_VAR})`,
                        fontWeight: 400,
                        lineHeight: 1.5,
                    }),
                    fontSize: scaledFontSize(14),
                    color: `var(${PRIMARY_PAGE_TEXT_COLOR_VAR})`,
                    margin: 0,
                }}
            >
                {review.reviewContent.text}
            </p>
        </div>
    )
}
